import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';

/* --------------------------------------------------------------------------
   Bluesky connect.

   The popup goes to api.schedpilot.com, which resolves the handle to its PDS,
   runs the OAuth exchange and stores the tokens server-side. It then posts a
   message back here and closes — nothing sensitive comes back through the URL.
   -------------------------------------------------------------------------- */

const API_ORIGIN = 'https://api.schedpilot.com';

function BlueskyConnect() {
  const { getAccountsData } = useAuth();

  const [open, setOpen] = useState(false);
  const [handle, setHandle] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const popupRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const finish = () => {
    setConnecting(false);
    clearInterval(pollRef.current);
  };

  const handleMessage = (event) => {
    // Only trust the API origin — anything else on the page can postMessage.
    if (event.origin !== API_ORIGIN) return;

    const { type, message, handle: connectedHandle } = event.data || {};

    if (type === 'bluesky-authenticated') {
      window.removeEventListener('message', handleMessage);
      finish();
      setOpen(false);
      setHandle('');
      toast.success(
        connectedHandle ? `Bluesky account @${connectedHandle} connected.` : 'Bluesky account connected.'
      );
      // Previously the list was never refreshed, so a successful connection
      // still looked like a failure until the user reloaded the page.
      getAccountsData();
    }

    if (type === 'bluesky-error') {
      window.removeEventListener('message', handleMessage);
      finish();
      setError(message || 'Could not connect your Bluesky account.');
    }
  };

  const startConnect = (e) => {
    e.preventDefault();

    const cleaned = handle.trim().replace(/^@/, '').replace(/^https?:\/\//i, '').toLowerCase();

    if (!cleaned || !cleaned.includes('.')) {
      setError('Enter your full Bluesky handle, for example yourname.bsky.social');
      return;
    }

    setError('');
    setConnecting(true);

    const width = 600, height = 720;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    popupRef.current = window.open(
      `${API_ORIGIN}/wp-json/api/v1/bluesky-login?handle=${encodeURIComponent(cleaned)}`,
      'BlueskyAuth',
      `width=${width},height=${height},top=${top},left=${left}`
    );

    if (!popupRef.current) {
      setConnecting(false);
      setError('Your browser blocked the popup. Allow popups for this site and try again.');
      return;
    }

    window.addEventListener('message', handleMessage);

    // If the user simply closes the window, stop the spinner rather than
    // leaving it running forever.
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        clearInterval(pollRef.current);
        setConnecting((was) => {
          if (was) getAccountsData();   // it may have succeeded just as it closed
          return false;
        });
      }
    }, 700);
  };

  return (
    <>
      <button className="btn btn-info" onClick={() => { setOpen(true); setError(''); }}>
        Connect Bluesky
      </button>

      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000,
          }}
          onClick={() => !connecting && setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 16, padding: 28,
              width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
          >
            <h5 style={{ fontWeight: 700, marginBottom: 6 }}>Connect Bluesky</h5>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: 18 }}>
              Enter your handle and we'll take you to Bluesky to approve access.
            </p>

            <form onSubmit={startConnect}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                Your Bluesky handle
              </label>
              <input
                className="form-control"
                autoFocus
                value={handle}
                disabled={connecting}
                onChange={(e) => { setHandle(e.target.value); setError(''); }}
                placeholder="yourname.bsky.social"
              />
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 6 }}>
                Custom domains work too, and so do self-hosted servers.
              </div>

              {error && (
                <div style={{
                  marginTop: 14, background: '#fef2f2', border: '1px solid #fecaca',
                  color: '#991b1b', borderRadius: 8, padding: '10px 12px', fontSize: '0.825rem',
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button
                  type="submit"
                  className="btn btn-info flex-grow-1"
                  disabled={connecting}
                >
                  {connecting ? 'Waiting for Bluesky…' : 'Connect'}
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  disabled={connecting}
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export { BlueskyConnect };
